import React from 'react';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, addDays,
  format, isSameMonth, isToday, startOfDay,
} from 'date-fns';
import { Plus } from 'lucide-react';
import { platformLabel } from '@/lib/clipPlatformFormats';

const STATUS_DOT = { draft: '#555E6E', ready: '#8A93A6', scheduled: '#3A7BFF', publishing: '#F5A623', published: '#39E6C8', failed: '#F5547C' };
const PLATFORM_DOT = { youtube: '#FF4D4D', instagram: '#E1306C', facebook: '#1877F2', tiktok: '#69C9D0' };

function PostChip({ post, onSelect, onDragStart, compact }) {
  return (
    <button
      draggable onDragStart={(e) => onDragStart(e, post)}
      onClick={(e) => { e.stopPropagation(); onSelect(post); }}
      className="w-full text-left px-1.5 py-1 rounded-md bg-white/5 hover:bg-white/10 border border-white/5 flex items-center gap-1.5 group"
      data-testid={`schedule-calendar-post-${post.id}`}
    >
      <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: PLATFORM_DOT[post.platform] || '#8A93A6' }} />
      <span className="text-[10px] text-[#8A93A6] shrink-0">{format(new Date(post.scheduledAt), 'h:mma')}</span>
      {!compact && <span className="text-[11px] text-white truncate flex-1">{post.contentTitle || post.title || 'Untitled'}</span>}
      <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: STATUS_DOT[post.status] || '#555E6E' }} title={post.status} />
    </button>
  );
}

function DayCell({ day, inMonth, posts, onSelect, onSelectSlot, onDrop, onDragOver, onDragStart, dense }) {
  const visible = posts.slice(0, dense ? 3 : 6);
  const overflow = posts.length - visible.length;
  return (
    <div
      onDragOver={onDragOver} onDrop={(e) => onDrop(e, day)}
      onClick={() => onSelectSlot(day)}
      className={`border border-white/5 p-1.5 flex flex-col gap-1 min-h-[86px] cursor-pointer hover:bg-white/[0.03] ${inMonth === false ? 'opacity-40' : ''}`}
      data-testid={`schedule-calendar-day-${format(day, 'yyyy-MM-dd')}`}
    >
      <div className="flex items-center justify-between">
        <span className={`text-[11px] ${isToday(day) ? 'text-[#3A7BFF] font-semibold' : 'text-[#8A93A6]'}`}>{format(day, 'd')}</span>
        {isToday(day) && <span className="text-[9px] text-[#3A7BFF]">Today</span>}
      </div>
      <div className="space-y-1 flex-1">
        {visible.map((p) => <PostChip key={p.id} post={p} onSelect={onSelect} onDragStart={onDragStart} compact={dense} />)}
        {overflow > 0 && <p className="text-[10px] text-[#555E6E]">+{overflow} more</p>}
        {posts.length === 0 && (
          <div className="opacity-0 hover:opacity-100 transition-opacity flex items-center gap-1 text-[10px] text-[#555E6E]"><Plus className="h-2.5 w-2.5" />Schedule here</div>
        )}
      </div>
    </div>
  );
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_START_HOUR = 6, DAY_END_HOUR = 23;

/**
 * The dominant calendar workspace (section 3). Month is a classic 6x7
 * grid. Week and Day render each day as a compact chronological column
 * rather than a full hour-by-hour grid — social posts don't need
 * minute-precise placement the way meetings do, and this keeps the UI
 * from turning into a second metadata form. Agenda is a flat
 * chronological list grouped by date. Drag-and-drop uses plain HTML5 DnD
 * — no calendar library dependency was added.
 */
export function ScheduleCalendarGrid({ view, currentDate, postsByDay, onSelectPost, onSelectSlot, onDropPost }) {
  const handleDragStart = (e, post) => { e.dataTransfer.setData('text/plain', post.id); };
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e, day) => {
    e.preventDefault();
    const postId = e.dataTransfer.getData('text/plain');
    const queueItemRaw = e.dataTransfer.getData('application/x-schedule-queue-item');
    if (queueItemRaw) { onDropPost({ queueItem: JSON.parse(queueItemRaw) }, day); return; }
    if (postId) onDropPost({ postId }, day);
  };

  const postsFor = (day) => postsByDay.get(format(day, 'yyyy-MM-dd')) || [];

  if (view === 'month') {
    const monthStart = startOfMonth(currentDate);
    const gridStart = startOfWeek(monthStart);
    const gridEnd = endOfWeek(endOfMonth(currentDate));
    const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
    return (
      <div data-testid="schedule-calendar-month">
        <div className="grid grid-cols-7 border-t border-l border-white/5">
          {WEEKDAY_LABELS.map((d) => <div key={d} className="text-[10px] uppercase tracking-wide text-[#555E6E] px-1.5 py-1 border-r border-b border-white/5">{d}</div>)}
          {days.map((day) => (
            <DayCell key={day.toISOString()} day={day} inMonth={isSameMonth(day, currentDate)} posts={postsFor(day)} onSelect={onSelectPost} onSelectSlot={onSelectSlot} onDrop={handleDrop} onDragOver={handleDragOver} onDragStart={handleDragStart} dense />
          ))}
        </div>
      </div>
    );
  }

  if (view === 'week') {
    const weekStart = startOfWeek(currentDate);
    const days = eachDayOfInterval({ start: weekStart, end: endOfWeek(currentDate) });
    return (
      <div className="grid grid-cols-7 gap-2" data-testid="schedule-calendar-week">
        {days.map((day) => (
          <div key={day.toISOString()} className="space-y-1.5">
            <div className={`text-center text-xs ${isToday(day) ? 'text-[#3A7BFF] font-semibold' : 'text-[#8A93A6]'}`}>{format(day, 'EEE d')}</div>
            <div onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, day)} onClick={() => onSelectSlot(day)} className="rounded-lg border border-white/5 p-1.5 space-y-1 min-h-[300px] cursor-pointer hover:bg-white/[0.02]" data-testid={`schedule-calendar-week-day-${format(day, 'yyyy-MM-dd')}`}>
              {postsFor(day).map((p) => <PostChip key={p.id} post={p} onSelect={onSelectPost} onDragStart={handleDragStart} />)}
              {postsFor(day).length === 0 && <p className="text-[10px] text-[#555E6E] text-center pt-4">No posts</p>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (view === 'day') {
    const hours = Array.from({ length: DAY_END_HOUR - DAY_START_HOUR + 1 }, (_, i) => DAY_START_HOUR + i);
    const posts = postsFor(currentDate).sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
    return (
      <div data-testid="schedule-calendar-day">
        <p className={`text-sm mb-2 ${isToday(currentDate) ? 'text-[#3A7BFF] font-semibold' : 'text-white'}`}>{format(currentDate, 'EEEE, MMMM d, yyyy')}</p>
        <div className="divide-y divide-white/5 border border-white/5 rounded-lg overflow-hidden">
          {hours.map((h) => {
            const hourPosts = posts.filter((p) => new Date(p.scheduledAt).getHours() === h);
            const slotDate = new Date(currentDate); slotDate.setHours(h, 0, 0, 0);
            return (
              <div key={h} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, slotDate)} onClick={() => onSelectSlot(slotDate)} className="flex gap-3 px-3 py-2 hover:bg-white/[0.02] cursor-pointer min-h-[40px]" data-testid={`schedule-calendar-hour-${h}`}>
                <span className="text-[10px] text-[#555E6E] w-14 shrink-0 pt-0.5">{format(slotDate, 'h a')}</span>
                <div className="flex-1 space-y-1">{hourPosts.map((p) => <PostChip key={p.id} post={p} onSelect={onSelectPost} onDragStart={handleDragStart} />)}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Agenda — flat chronological list across the next 30 days from currentDate.
  const rangeDays = Array.from({ length: 30 }, (_, i) => addDays(startOfDay(currentDate), i));
  const nonEmptyDays = rangeDays.filter((d) => postsFor(d).length > 0);
  return (
    <div className="space-y-3" data-testid="schedule-calendar-agenda">
      {nonEmptyDays.length === 0 ? (
        <p className="text-sm text-[#8A93A6] text-center py-10">Nothing scheduled in the next 30 days.</p>
      ) : nonEmptyDays.map((day) => (
        <div key={day.toISOString()}>
          <p className={`text-xs mb-1.5 ${isToday(day) ? 'text-[#3A7BFF] font-semibold' : 'text-[#8A93A6]'}`}>{format(day, 'EEEE, MMMM d')}</p>
          <div className="space-y-1">
            {postsFor(day).sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt)).map((p) => (
              <button key={p.id} onClick={() => onSelectPost(p)} className="w-full flex items-center gap-2 p-2 rounded-lg border border-white/5 hover:bg-white/5 text-left" data-testid={`schedule-agenda-post-${p.id}`}>
                <span className="h-2 w-2 rounded-full shrink-0" style={{ background: PLATFORM_DOT[p.platform] || '#8A93A6' }} />
                <span className="text-xs text-[#8A93A6] w-16 shrink-0">{format(new Date(p.scheduledAt), 'h:mm a')}</span>
                <span className="text-xs text-white truncate flex-1">{p.contentTitle || p.title || 'Untitled'}</span>
                <span className="text-[10px] text-[#555E6E] shrink-0">{platformLabel(p.platform)}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full shrink-0" style={{ background: `${STATUS_DOT[p.status]}22`, color: STATUS_DOT[p.status] }}>{p.status}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function groupPostsByDay(posts) {
  const map = new Map();
  posts.forEach((p) => {
    if (!p.scheduledAt) return;
    const key = format(new Date(p.scheduledAt), 'yyyy-MM-dd');
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(p);
  });
  return map;
}
